import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { fetchDepartments, fetchHealth, fetchRun, startQueryStream } from "./api/client";
import { Footer } from "./components/Footer";
import { Header } from "./components/Header";
import { LiveRunView } from "./components/LiveRunView";
import { QueryInput } from "./components/QueryInput";
import { LiveLogOverlay, ResultsView } from "./components/ResultsView";
import { loadSavedRun, RunHistoryModal } from "./components/RunHistoryModal";
import { initialRunState } from "./types/api";
import { runReducer } from "./state/runReducer";

export function App() {
  const [state, dispatch] = useReducer(runReducer, undefined, initialRunState);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loadingRun, setLoadingRun] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [clockMs, setClockMs] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    void Promise.all([fetchHealth(), fetchDepartments()]).then(([h, depts]) => {
      if (h?.vector_store) {
        dispatch({ type: "SET_DEFAULT_VECTOR_STORE", store: h.vector_store });
      }
      if (depts.length) {
        dispatch({ type: "SET_DEPARTMENTS", departments: depts });
        const defaultId = h?.default_department ?? depts[0].id;
        const defaultDept = depts.find((d) => d.id === defaultId) ?? depts[0];
        dispatch({
          type: "SET_DEPARTMENT",
          departmentId: defaultDept.id,
          departmentLabel: defaultDept.label,
        });
      }
    });
  }, []);

  useEffect(() => {
    if (state.runStatus !== "running" && state.runStatus !== "connecting") {
      if (state.durationMs != null) setClockMs(state.durationMs);
      return;
    }
    const start = state.runStartMs ?? Date.now();
    const tick = () => setClockMs(Date.now() - start);
    tick();
    const id = window.setInterval(tick, 200);
    return () => clearInterval(id);
  }, [state.runStatus, state.runStartMs, state.durationMs]);

  const toast = useCallback((msg: string) => {
    setToastMsg(msg);
    window.setTimeout(() => setToastMsg(null), 2500);
  }, []);

  const startRun = useCallback(
    (prompt: string, vectorStore = state.vectorStore, departmentId = state.departmentId) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const dept =
        state.departments.find((d) => d.id === departmentId) ??
        ({ id: departmentId, label: state.departmentLabel } as const);

      dispatch({
        type: "START_RUN",
        prompt,
        vectorStore,
        departmentId: dept.id,
        departmentLabel: dept.label,
      });

      let completedRunId: string | null = null;

      void startQueryStream(
        prompt,
        vectorStore,
        departmentId,
        (event) => {
          dispatch({ type: "SSE_EVENT", event });
          if (event.type === "run_start") {
            const d = event.data as { run_id?: string } | undefined;
            completedRunId = d?.run_id ?? completedRunId;
          }
          if (event.type === "run_end") {
            const d = event.data as { run_id?: string } | undefined;
            completedRunId = d?.run_id ?? completedRunId;
          }
          if (event.type === "done" && completedRunId) {
            const rid = completedRunId;
            void fetchRun(rid)
              .then((run) =>
                dispatch({ type: "LOAD_SAVED_RUN", run, preserveSession: true })
              )
              .catch(() => {
                /* keep live-streamed state if artifact fetch fails */
              });
          }
        },
        controller.signal
      ).catch((err) => {
        if (controller.signal.aborted) return;
        dispatch({
          type: "RUN_ERROR",
          message:
            err instanceof Error ? err.message : "Connection lost during the run.",
        });
      });
    },
    [state.vectorStore, state.departmentId, state.departmentLabel, state.departments]
  );

  const handleSubmit = () => {
    const prompt = state.prompt.trim();
    if (!prompt) return;
    startRun(prompt);
  };

  const handleCancel = () => {
    abortRef.current?.abort();
    dispatch({ type: "CANCEL_RUN" });
  };

  const handleOpenRun = async (runId: string) => {
    setLoadingRun(true);
    try {
      const run = await loadSavedRun(runId);
      dispatch({ type: "LOAD_SAVED_RUN", run });
    } catch (e) {
      dispatch({
        type: "LOAD_SAVED_RUN_ERROR",
        message: e instanceof Error ? e.message : "Could not load this run.",
      });
    } finally {
      setLoadingRun(false);
    }
  };

  const runInProgress =
    state.screen === "live" &&
    (state.runStatus === "running" || state.runStatus === "connecting");

  return (
    <div className="screen">
      <Header
        query={state.prompt}
        departmentLabel={state.screen !== "input" ? state.departmentLabel : undefined}
        showQuery={state.screen !== "input"}
        runInProgress={runInProgress}
        onRuns={() => setHistoryOpen(true)}
        onCancel={runInProgress ? handleCancel : undefined}
        onNewSearch={state.screen === "results" ? () => dispatch({ type: "RESET_TO_INPUT" }) : undefined}
      />

      {state.screen === "input" ? (
        <QueryInput
          prompt={state.prompt}
          vectorStore={state.vectorStore}
          defaultVectorStore={state.defaultVectorStore}
          departmentId={state.departmentId}
          departments={state.departments}
          onPromptChange={(p) => dispatch({ type: "SET_PROMPT", prompt: p })}
          onVectorStoreChange={(s) => dispatch({ type: "SET_VECTOR_STORE", store: s })}
          onDepartmentChange={(id, label) =>
            dispatch({ type: "SET_DEPARTMENT", departmentId: id, departmentLabel: label })
          }
          onSubmit={handleSubmit}
        />
      ) : null}

      {state.screen === "live" ? (
        <LiveRunView
          state={state}
          clockMs={clockMs}
          onRetry={() => startRun(state.prompt, state.vectorStore)}
          onEditQuery={() => dispatch({ type: "RESET_TO_INPUT" })}
        />
      ) : null}

      {state.screen === "results" ? (
        loadingRun ? (
          <main className="body-main">
            <div className="loading-skeleton">
              <div className="sk-line w92" />
              <div className="sk-line w78" />
              <div className="sk-line w60" />
            </div>
          </main>
        ) : (
          <ResultsView
            state={state}
            hasLiveLog={state.log.length > 0}
            onShowLiveLog={() => dispatch({ type: "SHOW_LIVE_LOG", show: true })}
            onFollowUp={(q) => {
              dispatch({ type: "SET_PROMPT", prompt: q });
              startRun(q);
            }}
            onNewSearch={() => dispatch({ type: "RESET_TO_INPUT" })}
            toast={toast}
          />
        )
      ) : null}

      <Footer />

      <RunHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onSelectRun={(id) => void handleOpenRun(id)}
        onStartSearch={() => dispatch({ type: "RESET_TO_INPUT" })}
      />

      {state.showLiveLogOverlay ? (
        <LiveLogOverlay
          state={state}
          onClose={() => dispatch({ type: "SHOW_LIVE_LOG", show: false })}
        />
      ) : null}

      {toastMsg ? <div className="toast">{toastMsg}</div> : null}
    </div>
  );
}
